type FixedPowerContentProps = {
  voltage: string;
  gradientId: string;
  outletGradientId: string;
  outletShadowId: string;
  rotation?: number;
  reflected?: boolean;
};

const CABLE_PATH = "M 0 5 C -0.8 9, 0.9 12, 0.1 16 C -0.5 19, -0.2 22, 2 24 C 5.5 27, 11 25, 16 25";

/** Inhalt des Festanschlusses im lokalen, auf den Rahmenmittelpunkt bezogenen Koordinatensystem. */
export function FixedPowerContent({
  voltage,
  gradientId,
  outletGradientId,
  outletShadowId,
  rotation = 0,
  reflected = false,
}: FixedPowerContentProps) {
  return (
    <g transform={`scale(${reflected ? -1 : 1} 1) rotate(${-rotation})`} pointerEvents="none">
      <text x={0} y={-4} fill="#475569" fontSize={4.5} fontWeight={700} textAnchor="middle" dominantBaseline="central">
        {voltage}
      </text>
      <path d={CABLE_PATH} fill="none" stroke={`url(#${gradientId})`} strokeWidth={3.4} strokeLinecap="butt" />
      <path
        d={CABLE_PATH}
        fill="none"
        stroke="#f4f7f9"
        strokeWidth={0.7}
        strokeLinecap="butt"
        opacity={0.7}
        transform="translate(-0.45 -0.2)"
      />
      <path
        d="M -4.8 6.8 H 4.8"
        fill="none"
        stroke="#5f666b"
        strokeWidth={1.8}
        opacity={0.5}
        filter={`url(#${outletShadowId})`}
      />
      <path d="M -5 6 C -5 0, 5 0, 5 6 Z" fill={`url(#${outletGradientId})`} />
    </g>
  );
}

type FixedPowerFrontViewProps = {
  voltage?: string;
  idPrefix: string;
  selected?: boolean;
  contentRotation?: number;
  contentReflected?: boolean;
};

/** Rahmen und separat gegenläufig drehbarer Inhalt eines Festanschlusses. Mittelpunkt ist (0, 0). */
export function FixedPowerFrontView({
  voltage = "230V",
  idPrefix,
  selected = false,
  contentRotation = 0,
  contentReflected = false,
}: FixedPowerFrontViewProps) {
  const cableGradientId = `${idPrefix}-cable`;
  const outletGradientId = `${idPrefix}-outlet`;
  const outletShadowId = `${idPrefix}-outlet-shadow`;
  return (
    <>
      <defs>
        <linearGradient id={cableGradientId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#77838d" />
          <stop offset="38%" stopColor="#bac4cb" />
          <stop offset="62%" stopColor="#89959e" />
          <stop offset="100%" stopColor="#626d76" />
        </linearGradient>
        <radialGradient id={outletGradientId} cx="50%" cy="25%" r="100%">
          <stop offset="0%" stopColor={selected ? "#fff8ec" : "#f7f8f9"} />
          <stop offset="48%" stopColor={selected ? "#ffdca1" : "#d7dbde"} />
          <stop offset="100%" stopColor={selected ? "#e9ad55" : "#aab1b6"} />
        </radialGradient>
        <filter id={outletShadowId} x="-30%" y="-100%" width="160%" height="300%">
          <feGaussianBlur stdDeviation="0.7" />
        </filter>
      </defs>
      <rect
        x={-14}
        y={-14}
        width={28}
        height={28}
        rx={3}
        fill={selected ? "#f5bd62" : "#ededed"}
        stroke={selected ? "#d98a22" : "#b9b9b9"}
        strokeWidth={1.1}
      />
      <path
        d="M -13 10.5 Q -11 14 -7 14 H 10 Q 14 14 14 10 V 12 Q 14 15 10 15 H -10 Q -14 15 -14 11 Z"
        fill={selected ? "#eda744" : "#d7d7d7"}
        opacity={0.75}
      />
      <rect
        x={-11.5}
        y={-11.5}
        width={23}
        height={23}
        rx={2.2}
        fill={selected ? "#fff8e9" : "#fafafa"}
        stroke={selected ? "#e9ad55" : "#bdbdbd"}
        strokeWidth={1}
      />
      <path
        d="M -10 -9.5 H 10"
        fill="none"
        stroke={selected ? "#fffaf0" : "#fff"}
        strokeWidth={1.2}
        strokeLinecap="round"
      />
      <FixedPowerContent
        voltage={voltage}
        gradientId={cableGradientId}
        outletGradientId={outletGradientId}
        outletShadowId={outletShadowId}
        rotation={contentRotation}
        reflected={contentReflected}
      />
    </>
  );
}
